import { uniqBy } from "lodash-es";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import type { ContactEvent } from "@/features/contact/types";
import {
  applyMediaReadyToMessage,
  MediaReadyPatch,
} from "@/features/inbox/helpers/apply-media-ready";
import {
  Message,
  MessageError,
  MessageReaction,
  MessageStatusRecord,
} from "@/types/message";
import type { Activity } from "@/types/activity";
import { hasEventPayload, sortEvents } from "@/utils/event";

interface EventState {
  events: Record<string, Array<ContactEvent>>;
  addEvents: (id: string, events: Array<ContactEvent>) => void;
  addEvent: (id: string, event: ContactEvent) => void;
  updateActivity: (contactId: string, activity: Activity) => void;
  upsertStatus: (
    id: string,
    messageId: string,
    statuses: Array<MessageStatusRecord>,
    error?: MessageError,
  ) => void;
  upsertMessage: (contactId: string, message: Message) => void;
  upsertReactions: (
    contactId: string,
    messageId: string,
    reactions: MessageReaction[],
  ) => void;
  applyMediaReady: (messageId: string, patch: MediaReadyPatch) => void;
  removeEvent: (contactId: string, eventId: string) => void;
  removeMessage: (contactId: string, messageId: string) => void;
}

export const useEventStore = create<EventState>()(
  immer((set) => ({
    events: {},
    addEvent(id, event) {
      if (!hasEventPayload(event)) return;

      set((state) => {
        state.events[id] = uniqBy(
          sortEvents([event, ...(state.events[id] ?? [])]),
          "id",
        );
      });
    },
    updateActivity(contactId, activity) {
      set((state) => {
        const events = state.events[contactId];
        if (!events) return;

        events.forEach((event) => {
          if (event.type === "activity" && event.activity.id === activity.id) {
            event.activity = activity;
          }
        });
      });
    },
    upsertStatus(
      id: string,
      messageId: string,
      statuses: Array<MessageStatusRecord>,
      error?: MessageError,
    ) {
      set((state) => {
        const events = state.events[id];

        if (!events) return;

        events.forEach((event) => {
          if (event.type === "message") {
            if (event.message.id === messageId) {
              event.message.statuses = uniqBy(
                [...event.message.statuses, ...statuses],
                "status",
              );

              if (error) {
                event.message.error = error;
              }
            }
          }
        });
      });
    },
    addEvents(id, events) {
      const validEvents = events.filter(hasEventPayload);

      set((state) => ({
        ...state,
        events: {
          ...(state.events ? state.events : {}),
          [id]: uniqBy(
            sortEvents([
              ...(state.events[id] ? state.events[id] : []),
              ...validEvents,
            ]),
            "id",
          ),
        },
      }));
    },
    upsertMessage(contactId: string, message: Message) {
      set((state) => {
        if (!state.events[contactId]) {
          state.events[contactId] = [];
        }

        const events = state.events[contactId];

        const existing = events.find(
          (event) =>
            event.type === "message" && event.message.id === message.id,
        );

        if (existing && existing.type === "message") {
          existing.message = message;
        } else {
          events.unshift({
            id: message.id,
            type: "message",
            message,
            contactId,
            createdAt: message.createdAt,
          });
        }
      });
    },
    upsertReactions(
      contactId: string,
      messageId: string,
      reactions: MessageReaction[],
    ) {
      set((state) => {
        const events = state.events[contactId];

        if (!events) return;

        events.forEach((event) => {
          if (event.type === "message" && event.message.id === messageId) {
            event.message.reactions = reactions;
          }
        });
      });
    },
    applyMediaReady(messageId: string, patch: MediaReadyPatch) {
      set((state) => {
        for (const events of Object.values(state.events)) {
          for (const event of events) {
            if (event.type === "message" && event.message.id === messageId) {
              event.message = applyMediaReadyToMessage(event.message, patch);
            }
          }
        }
      });
    },
    removeEvent(contactId: string, eventId: string) {
      set((state) => {
        const events = state.events[contactId];
        if (!events) return;
        state.events[contactId] = events.filter((event) => event.id !== eventId);
      });
    },
    removeMessage(contactId: string, messageId: string) {
      set((state) => {
        const events = state.events[contactId];

        if (!events) return;

        state.events[contactId] = events.filter(
          (event) =>
            !(event.type === "message" && event.message.id === messageId),
        );
      });
    },
  })),
);
